import { z } from 'zod';
import { ResourceLibraryAssetItem } from '../types/resourceLibraryItems';
import { AssetBase } from '../types/Asset';
import {
    GetResourceLibraryAssetItemSchema,
    ResourceLibraryAssetItemSchema,
} from '../schemas/resourceLibraryAssetItemZodSchemas';
import {
    ProductGroupSchema,
    ProductApplicationSchema,
    NavigationNodeSchema,
} from '../schemas/resourceLibraryBaseZodSchemas';
import { LOCALES, LocaleCode } from '../../../../../utils/Locale';
import { AssetBaseSchema } from '../schemas/assetZodSchemas';

function toCapitalizedLocaleSuffix(locale: string): string {
    return locale
        .split('-')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join('');
}

type ZodAssetItem = z.infer<typeof ResourceLibraryAssetItemSchema>;

function normalizeLocaleAsset(
    item: ZodAssetItem,
    locale: LocaleCode,
    returnNullIfSameAsFallback: boolean
): AssetBase | null {
    // I've set 'en' as the fallback language for all languages in Pimcore.
    // GraphQL queries now return fallback language asset in the responses if
    // there is no asset for the requested language. We could set
    // `getFallbackLanguageValue false` in the GraphQL queries to avoid this,
    // but this seems to work only for object, not for assets, so we need to
    // filter them out to prevent treating asset items falsely as having each
    // language variant asset (e.g. to prevent displaying all asset language
    // buttons)
    const PIMCORE_RETURNS_FALLBACK_LANGUAGE_ASSET = true;
    const fallbackLang = 'en';
    const fallbackLangKey = ('assetLang' +
        toCapitalizedLocaleSuffix(fallbackLang)) as keyof ZodAssetItem;
    const fallbackFullpath = item[fallbackLangKey]?.fullpath ?? null;

    const key = `assetLang${toCapitalizedLocaleSuffix(locale)}` as keyof ZodAssetItem;
    const zodAsset: z.infer<typeof AssetBaseSchema> | null = item[key] ?? null;

    if (
        PIMCORE_RETURNS_FALLBACK_LANGUAGE_ASSET &&
        returnNullIfSameAsFallback &&
        locale !== fallbackLang &&
        zodAsset?.fullpath === fallbackFullpath
    ) {
        return null;
    }
    return zodAsset
        ? {
              fullpath: zodAsset.fullpath ?? null,
              mimeType: zodAsset.mimetype ?? null,
              type: zodAsset.type ?? null,
          }
        : null;
}

export function hydrateResourceLibraryAssetItem(
    item: ZodAssetItem,
    localeCode: LocaleCode
): ResourceLibraryAssetItem {
    // Build the language‐variant assets: en, de, ja, zhCn, zhTw, etc.
    const assetByLanguage = Object.fromEntries(
        LOCALES.map(({ locale }) => {
            const asset = normalizeLocaleAsset(item, locale, true);
            return [locale, asset];
        })
    ) as Record<LocaleCode, AssetBase | null>;

    // Build the default asset for the current language
    let asset: AssetBase | null = assetByLanguage[localeCode];
    if (!asset) {
        // If the default asset is missing, use the first available
        const firstAvailableAsset = Object.values(assetByLanguage).find((a) => a);
        asset = firstAvailableAsset ?? null;
    }

    const assetItem: ResourceLibraryAssetItem = {
        kind: 'asset',
        id: item.id,
        key: item.key,
        name: item.name,
        description: item.description ?? '',
        brandId: item.brand ? item.brand.id : null,
        productGroupIds: item.productGroup
            ? item.productGroup.map((pg: z.infer<typeof ProductGroupSchema>) => pg.id)
            : [],
        productApplicationIds: item.productApplication
            ? item.productApplication.map((pa: z.infer<typeof ProductApplicationSchema>) => pa.id)
            : [],
        resourceTypeId: item.resourceType.id,
        navigationScopeIds: item.navigationScope
            ? item.navigationScope.map((ns: z.infer<typeof NavigationNodeSchema>) => ns.id)
            : [],
        exclusiveNavigationScopeIds: item.exclusiveNavigationScope
            ? item.exclusiveNavigationScope.map(
                  (ens: z.infer<typeof NavigationNodeSchema>) => ens.id
              )
            : [],
        expirationDate: item.expirationDate,

        validFromDate: item.validFromDate,
        publicationDate: item.publicationDate,
        lastModificationDate: item.lastModificationDate,
        thumbnailUrl: item.thumbnailUrl,
        asset,
        assetByLanguage,
    };

    return assetItem;
}

/**
 * Transforms and validates the response from the getResourceLibraryAssetItem GraphQL query
 * to a simplified ResourceLibraryAssetItem type.
 *
 * @param data The response data from the getResourceLibraryItemListing query
 * @returns An array of valid ResourceLibraryAssetItem objects
 */
export function transformResourceLibraryAssetItemResponse(
    data: unknown, // GetResourceLibraryAssetItem - use unknown for jest tests be able to
    // pass invalid data
    localeCode: LocaleCode
): ResourceLibraryAssetItem | null {
    // Parse and validate the data using the Zod schema
    const parsedResult = GetResourceLibraryAssetItemSchema.safeParse(data);

    if (!parsedResult.success) {
        console.error(
            'Validation failed for getResourceLibraryAssetItemL:',
            parsedResult.error.errors
        );
        return null;
    }

    // Transform resource item to simplified ResourceLibraryAssetItem type
    const item = parsedResult.data.getResourceLibraryItem;
    return hydrateResourceLibraryAssetItem(item, localeCode);
}
